import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiErrorMessage } from "@/api/perkpoint/client";
import { RulesApi, ruleKeys, type NativeRulePart } from "@/api/perkpoint/rules";
import { useActiveProgramCurrency, useActiveProgramId } from "../programs/program-scope";
import { cn } from "@/lib/utils";
import { EVENT_LABELS, PRODUCT_OPTIONS, nativeEarnPayload } from "./eventEarn";

const ruleFormSchema = z.object({
  ruleTitle: z.string().min(1, "Rule title is required"),
  pointsIssuanceType: z.string().optional(),
  minInvoiceAmount: z.string().optional(),
  awardPoints: z.string().optional(),
  triggerEvent: z.string().optional(),
  pointsRelease: z.string().optional(),
  releaseDays: z.string().optional(),
  productType: z.string().optional(),
  pointsValidityType: z.string().optional(),
  pointsValidityDays: z.string().optional(),
  minInvoiceAmountRedemption: z.string().optional(),
  maxRedemptionPoints: z.string().optional(),
  redemptionCriteria: z.string().optional(),
  earnWhileRedeem: z.string().optional(),
});

type RuleFormValues = z.infer<typeof ruleFormSchema>;

interface CreateRuleFormProps {
  onSuccess?: () => void;
  onCancel?: () => void;
  editMode?: boolean;
  initialValues?: {
    ruleId?: string;
    title?: string;
    description?: string;
    earnRule?: {
      pointsIssuanceType?: string;
      minInvoiceAmount?: number;
      awardPoints?: number;
      triggerEvent?: string;
      release?: "immediate" | "checkout";
      releaseDays?: number;
      productType?: string;
      tierCredits?: string[];
    };
    validityRule?: {
      pointsValidityType?: string;
      pointsValidityDays?: number;
    };
    redemptionRule?: {
      minInvoiceAmount?: number;
      maxRedemptionPoints?: number;
      redemptionCriteria?: string;
      earnWhileRedeem?: string;
    };
  };
}

const fieldClass =
  "h-11 rounded-xl border-slate-200 bg-white shadow-none placeholder:text-slate-400";

const EARN_WHILE_OPTIONS = [
  {
    value: "NoEarn",
    label: "None",
    hint: "An order that redeems points does not earn any points.",
  },
  {
    value: "PartialEarn",
    label: "Partial",
    hint: "Points are earned only on the amount not paid with points.",
  },
  {
    value: "FullEarn",
    label: "Full",
    hint: "Points are earned on the full invoice, even when points are redeemed.",
  },
];

function choiceClass(active: boolean) {
  return cn(
    "rounded-2xl border px-3 py-3 text-left text-sm",
    active
      ? "border-emerald-700 bg-emerald-50 font-medium text-emerald-950"
      : "border-slate-200 text-slate-700",
  );
}

export function CreateRuleForm({
  onSuccess,
  onCancel,
  editMode = false,
  initialValues,
}: CreateRuleFormProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { programId } = useActiveProgramId();
  const { currency } = useActiveProgramCurrency();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(
    initialValues?.earnRule
      ? "earn"
      : initialValues?.redemptionRule
        ? "redemption"
        : initialValues?.validityRule
          ? "validity"
          : "earn",
  );

  const form = useForm<RuleFormValues>({
    resolver: zodResolver(ruleFormSchema),
    defaultValues: {
      ruleTitle: initialValues?.title || "",
      pointsIssuanceType:
        initialValues?.earnRule?.pointsIssuanceType || "FixedPointsPerTxn",
      minInvoiceAmount:
        initialValues?.earnRule?.minInvoiceAmount?.toString() || "",
      awardPoints: initialValues?.earnRule?.awardPoints?.toString() || "",
      triggerEvent: initialValues?.earnRule?.triggerEvent || "payment.captured",
      pointsRelease: initialValues?.earnRule?.release || "immediate",
      releaseDays: initialValues?.earnRule?.releaseDays?.toString() || "1",
      productType: initialValues?.earnRule?.productType || "any",
      pointsValidityType:
        initialValues?.validityRule?.pointsValidityType || "Fixed",
      pointsValidityDays:
        initialValues?.validityRule?.pointsValidityDays?.toString() || "",
      minInvoiceAmountRedemption:
        initialValues?.redemptionRule?.minInvoiceAmount?.toString() || "",
      maxRedemptionPoints:
        initialValues?.redemptionRule?.maxRedemptionPoints?.toString() || "",
      redemptionCriteria:
        initialValues?.redemptionRule?.redemptionCriteria || "%OfInvoiceAmount",
      earnWhileRedeem: initialValues?.redemptionRule?.earnWhileRedeem || "NoEarn",
    },
  });

  const issuanceType = form.watch("pointsIssuanceType");
  const earnWhile = form.watch("earnWhileRedeem");
  const pointsRelease = form.watch("pointsRelease");
  const tierCredits = initialValues?.earnRule?.tierCredits ?? [];
  const eventsQuery = useQuery({
    queryKey: ["perkpoint", "event-types", programId],
    queryFn: () => RulesApi.eventTypes(programId!),
    enabled: Boolean(programId),
  });
  const selectedEvent = form.watch("triggerEvent");
  const loadedEvents = (eventsQuery.data?.length
    ? eventsQuery.data
    : [
        { key: "booking.completed", name: "Booking completed" },
        { key: "payment.captured", name: "Payment captured" },
        { key: "member.enrolled", name: "Member enrolled" },
      ]
  ).map((event) => ({
    key: event.key,
    name: event.name || EVENT_LABELS[event.key] || event.key,
  }));
  const eventOptions = selectedEvent && !loadedEvents.some((event) => event.key === selectedEvent)
    ? [{ key: selectedEvent, name: EVENT_LABELS[selectedEvent] || selectedEvent }, ...loadedEvents]
    : loadedEvents;

  const onSubmit = async (data: RuleFormValues) => {
    if (activeTab === "earn") {
      const multiplierOnly = data.pointsIssuanceType === "Multiplier";
      if (!data.awardPoints?.trim() || (!multiplierOnly && !data.minInvoiceAmount?.trim())) {
        toast({
          title: "Earn rule is incomplete",
          description: multiplierOnly
            ? "Enter the multiplier."
            : "Enter the minimum invoice and the points to give.",
          variant: "destructive",
        });
        return;
      }
      if (
        data.pointsIssuanceType === "%OfInvoiceAmount" &&
        Number(data.awardPoints) > 100
      ) {
        toast({
          title: "Earn rule is incomplete",
          description: "Invoice percentage cannot be more than 100.",
          variant: "destructive",
        });
        return;
      }
      if (data.pointsRelease === "checkout" && data.releaseDays?.trim() && Number(data.releaseDays) < 0) {
        toast({
          title: "Earn rule is incomplete",
          description: "Days after checkout cannot be negative.",
          variant: "destructive",
        });
        return;
      }
    }
    if (activeTab === "validity" && !data.pointsValidityDays?.trim()) {
      toast({
        title: "Validity rule is incomplete",
        description: "Enter how many days the points stay valid.",
        variant: "destructive",
      });
      return;
    }
    if (activeTab === "redemption" && !data.maxRedemptionPoints?.trim()) {
      toast({
        title: "Redemption rule is incomplete",
        description: "Enter the maximum points on one order.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      if (!programId) throw new Error("No program selected.");
      const criteria =
        data.redemptionCriteria === "%OfAvailablePoints"
          ? "percent_of_points"
          : data.redemptionCriteria === "NoCriteria"
            ? "none"
            : "percent_of_invoice";
      const earnWhileRedeem =
        data.earnWhileRedeem === "FullEarn"
          ? "full"
          : data.earnWhileRedeem === "PartialEarn"
            ? "partial"
            : "none";
      const payload: NativeRulePart = {
        name: data.ruleTitle,
        ...(activeTab === "earn" && {
          earn: nativeEarnPayload({
            pointsIssuanceType: data.pointsIssuanceType || "FixedPointsPerTxn",
            awardPoints: data.awardPoints || "",
            minInvoiceAmount: data.minInvoiceAmount || "0",
            triggerEvent: data.triggerEvent || "payment.captured",
            release: data.pointsRelease || "immediate",
            releaseDays: data.releaseDays || "1",
            productType: data.productType || "any",
          }),
        }),
        ...(activeTab === "redemption" && {
          redemption: {
            min_invoice_amount: data.minInvoiceAmountRedemption?.trim()
              ? Number(data.minInvoiceAmountRedemption)
              : null,
            max_points_per_order: Number(data.maxRedemptionPoints),
            criteria,
            earn_while_redeem: earnWhileRedeem,
          },
        }),
        ...(activeTab === "validity" && {
          validity: {
            type: data.pointsValidityType === "Revolving" ? "revolving" : "fixed",
            days: Number(data.pointsValidityDays),
          },
        }),
      };

      const saved =
        editMode && initialValues?.ruleId
          ? await RulesApi.update(programId, initialValues.ruleId, payload)
          : await RulesApi.create(programId, payload);
      queryClient.invalidateQueries({ queryKey: ruleKeys.all });
      toast({
        title: editMode ? "Rule updated" : "Rule created",
        description: editMode && saved.has_pending_changes
          ? `${saved.code}: saved as a new draft. The live version keeps running until it is approved.`
          : `${saved.code} is a draft. Submit it for approval to put it live.`,
      });
      form.reset();
      setActiveTab("earn");
      onSuccess?.();
    } catch (error) {
      toast({
        title: "Error",
        description: apiErrorMessage(
          error,
          editMode ? "Failed to update rule" : "Failed to create rule",
        ),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.reset();
    setActiveTab("earn");
    onCancel?.();
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {(
            [
              ["earn", "Earn", "Points issued when an event comes in"],
              ["redemption", "Redemption", "Policy for spending points"],
              ["validity", "Validity", "How long points last"],
            ] as const
          ).map(([id, label, hint]) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveTab(id)}
              className={cn(
                "rounded-2xl border px-3 py-3 text-left",
                activeTab === id
                  ? "border-emerald-700 bg-emerald-50"
                  : "border-slate-200 bg-white",
              )}
            >
              <p className="text-sm font-semibold text-slate-950">{label}</p>
              <p className="mt-0.5 text-xs text-slate-500">{hint}</p>
            </button>
          ))}
        </div>

        <FormField
          control={form.control}
          name="ruleTitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Rule name</FormLabel>
              <FormControl>
                <Input
                  placeholder={
                    activeTab === "earn" ? "e.g. Weekend earn" : "e.g. Checkout redemption"
                  }
                  className={fieldClass}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {activeTab === "earn" ? (
          <>
            <FormField
              control={form.control}
              name="triggerEvent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Runs when</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={fieldClass}>
                        <SelectValue placeholder="Choose an event" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {eventOptions.map((event) => (
                        <SelectItem key={event.key} value={event.key}>
                          {event.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs leading-relaxed text-slate-500">
                    Member bookings send Booking completed. Payments send Payment captured.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pointsIssuanceType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>How points are calculated</FormLabel>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => field.onChange("FixedPointsPerTxn")}
                      className={choiceClass(field.value === "FixedPointsPerTxn")}
                    >
                      Points per order
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange("%OfInvoiceAmount")}
                      className={choiceClass(field.value === "%OfInvoiceAmount")}
                    >
                      % of invoice amount
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange("PointsPerSpend")}
                      className={choiceClass(field.value === "PointsPerSpend")}
                    >
                      Points per 100 {currency || "currency"}
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange("Multiplier")}
                      className={choiceClass(field.value === "Multiplier")}
                    >
                      Bonus multiplier
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="grid gap-4 sm:grid-cols-2">
              {issuanceType === "Multiplier" ? null : (
                <FormField
                  control={form.control}
                  name="minInvoiceAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Minimum invoice amount ({currency || "program currency"})</FormLabel>
                      <FormControl>
                        <Input type="number" min={0} placeholder="0" className={fieldClass} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="awardPoints"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {issuanceType === "%OfInvoiceAmount"
                        ? "Percent of invoice"
                        : issuanceType === "PointsPerSpend"
                          ? `Points for every 100 ${currency || "currency"}`
                          : issuanceType === "Multiplier"
                            ? "Multiplier"
                            : "Points to give"}
                    </FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={issuanceType === "Multiplier" ? 0.1 : 1}
                        step={issuanceType === "Multiplier" ? "0.1" : "1"}
                        placeholder={
                          issuanceType === "%OfInvoiceAmount"
                            ? "5"
                            : issuanceType === "PointsPerSpend"
                              ? "5"
                              : issuanceType === "Multiplier"
                                ? "2"
                                : "10"
                        }
                        className={fieldClass}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="productType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Applies to</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={fieldClass}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {PRODUCT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pointsRelease"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>When points become available</FormLabel>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button
                      type="button"
                      onClick={() => field.onChange("immediate")}
                      className={choiceClass(field.value === "immediate")}
                    >
                      Immediately
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange("checkout")}
                      className={choiceClass(field.value === "checkout")}
                    >
                      After checkout
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {pointsRelease === "checkout" ? (
              <FormField
                control={form.control}
                name="releaseDays"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Days after checkout</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="1" className={fieldClass} {...field} />
                    </FormControl>
                    <p className="text-xs leading-relaxed text-slate-500">
                      Points stay pending until this many days after checkout, then move into the
                      available balance on their own. 1 means the day after checkout. A program hold
                      or cancellation period, when set, can only make that later.
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : (
              <p className="text-xs leading-relaxed text-slate-500">
                Points are spendable as soon as the event is processed. Choose After checkout when
                a booking should stay pending until the stay ends.
              </p>
            )}
            {issuanceType === "Multiplier" ? (
              <p className="text-xs leading-relaxed text-slate-500">
                This rule does not award points on its own. It multiplies points from the other earn
                rules on the same event. 2 doubles them.
              </p>
            ) : issuanceType === "PointsPerSpend" ? (
              <p className="text-xs leading-relaxed text-slate-500">
                5 means 5 points for every 100 {currency || "currency"} of the booking. Amounts below
                the minimum invoice do not earn.
              </p>
            ) : (
              <p className="text-xs leading-relaxed text-slate-500">
                Orders below the minimum invoice do not earn. Qualifying orders receive{" "}
                {issuanceType === "%OfInvoiceAmount"
                  ? "points equal to this percent of the invoice amount"
                  : "a fixed number of points"}
                .
              </p>
            )}
            {tierCredits.length ? (
              <p className="rounded-xl bg-[#F7F4EE] px-4 py-3 text-sm leading-relaxed text-slate-600">
                Saving keeps the tier credits on this rule ({tierCredits.join(" and ")}).
              </p>
            ) : null}
          </>
        ) : null}

        {activeTab === "redemption" ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="minInvoiceAmountRedemption"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum invoice to redeem ({currency || "program currency"})</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} placeholder="Optional" className={fieldClass} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maxRedemptionPoints"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Max points on one order</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} placeholder="2000" className={fieldClass} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <FormField
              control={form.control}
              name="redemptionCriteria"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Usage limit</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={fieldClass}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="%OfInvoiceAmount">% of invoice value</SelectItem>
                      <SelectItem value="%OfAvailablePoints">% of points balance</SelectItem>
                      <SelectItem value="NoCriteria">No limit</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="earnWhileRedeem"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Earn while redeem</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={fieldClass}>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {EARN_WHILE_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs leading-relaxed text-slate-500">
                    {EARN_WHILE_OPTIONS.find((option) => option.value === earnWhile)?.hint}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        ) : null}

        {activeTab === "validity" ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="pointsValidityType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Validity type</FormLabel>
                  <div className="grid gap-2">
                    <button
                      type="button"
                      onClick={() => field.onChange("Fixed")}
                      className={choiceClass(field.value === "Fixed")}
                    >
                      Fixed
                    </button>
                    <button
                      type="button"
                      onClick={() => field.onChange("Revolving")}
                      className={choiceClass(field.value === "Revolving")}
                    >
                      Revolving
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="pointsValidityDays"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Points stay valid (days)</FormLabel>
                  <FormControl>
                    <Input type="number" min={1} placeholder="30" className={fieldClass} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        ) : null}

        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-emerald-700 text-white hover:bg-emerald-800"
          >
            {isSubmitting ? "Saving..." : editMode ? "Save changes" : "Add rule"}
          </Button>
        </div>
      </form>
    </Form>
  );
}
